// The article body, made readable — the client's posts are WordPress exports, and the export's
// furniture fights the design (client, 2026-10-13: "fix article formatting … so it looks nicer
// and read nicer").
//
// What the exports carry, and what this script does about it:
//   * every block is wrapped in divs — wp-block-group, entry-content, wp-block-post-content —
//     which DEFEAT the stylesheet: every .v2-rich rule is a direct-child selector (.v2-rich > p,
//     .v2-rich > * + *), so a paragraph inside a wrapper gets no measure, no rhythm and no
//     heading styles, and the whole post reads as one undifferentiated wall. The wrappers are
//     unwrapped: children lifted into the measure, the div dropped.
//   * the post's own title rides along as an h1 (the Maker Hotel's says "Weekend Vacanzas : The
//     Maker Hotel", Istanbul's is empty) — the article head already prints the title, so every h1
//     in the prose goes.
//   * empty paragraphs (one is a bare &nbsp;) and the CDATA tail ("]]&gt;") from the export's
//     serializer — both removed.
//   * stray <meta>/<link> elements from the WordPress head — removed; a body cannot use them.
//   * loose runs of inline markup at the top level (a bare sentence, a <span>, a <br> left as a
//     spacer between blocks) — the stylesheet's measure, rhythm and heading rules are all
//     direct-child selectors, so a run that is not in a paragraph reads tight against its
//     neighbours, and a top-level <br> inflates the rhythm to 44px where the design asks 28.
//     Runs are wrapped in a <p>; the <br> ends the paragraph instead of sitting in it.
//   * links that print their own address — a WordPress paste drops the whole tracking URL in as
//     the label ("https://www.airbnb.com/rooms/6665844?c=.pi80…", 250 characters of it). The
//     destination is kept in href and title; the label becomes the host and path.
//
// Runs on article and page templates (layout/theme.liquid bakes it in there). Content the reader
// can see is never touched — only how the export expresses it.
//
// Page bodies are merchant rich text rather than a WordPress export, so they take only the last
// step (image hygiene); everything above it is article-shaped and gated on the article container.
(() => {
  const scopes = [...document.querySelectorAll('.v2-artbody__measure, .v2-body')];
  if (!scopes.length) return;

  for (const scope of scopes) {
    if (scope.classList.contains('v2-artbody__measure')) normalizeArticle(scope);
    for (const img of scope.querySelectorAll('img')) {
      if (!img.hasAttribute('alt')) img.setAttribute('alt', '');
      if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
      if (!img.hasAttribute('loading') && !img.hasAttribute('fetchpriority')) img.setAttribute('loading', 'lazy');
      sizeUp(img);
    }
  }

  // A merchant image arrives without dimensions, and a box that is 0 tall until the photograph
  // lands is a layout shift. The natural size is right when it is known; before that the Shopify
  // CDN's own size suffix ("_480x480.jpg") answers, which is where most of them come from.
  function sizeUp(img) {
    if (img.getAttribute('width') && img.getAttribute('height')) return;
    const set = (w, h) => {
      if (!w || !h || img.getAttribute('width')) return;
      img.setAttribute('width', w);
      img.setAttribute('height', h);
    };
    if (img.complete && img.naturalWidth) set(img.naturalWidth, img.naturalHeight);
    else img.addEventListener('load', () => set(img.naturalWidth, img.naturalHeight), { once: true });
    const m = /_(\d+)x(\d+)\.[a-z]+(?:\?|$)/i.exec(img.currentSrc || img.src || '');
    if (m) set(Number(m[1]), Number(m[2]));
  }

  function normalizeArticle(scope) {
    const WP_WRAPPERS = ['wp-block-group', 'entry-content', 'wp-block-post-content'];
    const INLINE = new Set(['A', 'ABBR', 'B', 'BDI', 'BDO', 'CITE', 'CODE', 'DATA', 'DEL', 'DFN', 'EM', 'I',
                            'INS', 'KBD', 'MARK', 'Q', 'S', 'SAMP', 'SMALL', 'SPAN', 'STRONG', 'SUB', 'SUP',
                            'TIME', 'U', 'VAR', 'WBR']);

      // 1 · unwrap the WordPress wrappers, deepest-first so nested wrappers resolve in one pass
      for (const el of [...scope.querySelectorAll('div')].reverse()) {
        if (WP_WRAPPERS.some((c) => el.classList.contains(c))) el.replaceWith(...el.childNodes);
    }

    // 2 · drop furniture: the duplicate h1, empty headings and paragraphs, serializer tails
    for (const el of scope.querySelectorAll('h1')) el.remove();
    for (const el of scope.querySelectorAll('h2, h3, h4, h5, h6, p')) {
      const empty = !el.textContent.replace(/\u00a0/g, ' ').trim();
      if (empty && !el.querySelector('img, svg, video, iframe')) el.remove();
    }
    for (const node of [...scope.childNodes]) {
      if (node.nodeType === Node.TEXT_NODE && /^[\s\u00a0]*(?:\]\]>|\]\]&gt;)[\s\u00a0]*$/.test(node.textContent)) node.remove();
    }
    for (const el of scope.querySelectorAll('meta, link')) el.remove();

    // 3 · gather loose inline runs into paragraphs; a top-level <br> is a break between them
    let run = [];
    const flush = () => {
      if (!run.length) return;
      const p = document.createElement('p');
      run[0].before(p);
      for (const node of run) p.append(node);
      run = [];
    };
    for (const node of [...scope.childNodes]) {
      const text = node.nodeType === Node.TEXT_NODE;
      // formatting whitespace between blocks: only droppable outside a run, where it would be
      // the space between two words
      if (text && !run.length && !node.textContent.trim()) { node.remove(); continue; }
      const br = node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR';
      if (br) { flush(); node.remove(); continue; }
      if (text || (node.nodeType === Node.ELEMENT_NODE && INLINE.has(node.tagName))) { run.push(node); continue; }
      flush();
    }
    flush();

    // 4 · a link labelled with its own address reads as a wall of parameters
    for (const a of scope.querySelectorAll('a[href]')) {
      const label = a.textContent.trim();
      const href = a.getAttribute('href') || '';
      if (!/^https?:\/\//i.test(href) || !/^(https?:\/\/|www\.)/i.test(label)) continue;
      let pretty = href;
      try {
        const u = new URL(href);
        pretty = u.host.replace(/^www\./, '') + u.pathname.replace(/\/$/, '');
      } catch { /* an unparsable href is left as the label */ }
      a.textContent = pretty;
      a.setAttribute('title', href);
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener');
    }
  }
})();
